<template>
  <div class="dashboard dashboard-lab">
    <b-container fluid class="dashboard-lab-container">
      <!-- Barra de herramientas -->
      <section class="outside-toolbar">
        <button class="btn btn-primary btn-sm" @click="toggleCreator">
          <i class="material-icons">add</i> Crear etiqueta
        </button>
        <span class="tag-counter">Etiquetas: {{ tags.length }}/50</span>
        <button v-if="editingRatioTagId" class="btn btn-outline-secondary btn-sm" @click="cancelRatioEdit">
          Cancelar edición
        </button>
      </section>

      <!-- Panel de creación -->
      <section v-if="showCreator" class="creator-panel">
        <div class="creator-card">
          <h5>Nueva etiqueta</h5>
          <div class="creator-grid">
            <div>
              <label class="form-label">Título</label>
              <input
                v-model="draft.title"
                class="form-control form-control-sm"
                placeholder="Ej: Indicador de Liquidez"
              />
            </div>
            <div>
              <label class="form-label">Tipo</label>
              <select v-model="draft.presentationType" class="form-select form-select-sm">
                <option value="pie">Pastel</option>
                <option value="donut">Dona</option>
                <option value="bars">Barras</option>
                <option value="bars_horizontal">Barras horizontales</option>
                <option value="line">Línea</option>
                <option value="area">Área</option>
                <option value="gauge">Medidor</option>
                <option value="progress">Progreso</option>
                <option value="ranking">Ranking</option>
                <option value="value">Valor numérico</option>
                <option value="ratio">Ratio Financiero</option>
              </select>
            </div>
            <div>
              <label class="form-label">Color de fondo</label>
              <input v-model="draft.color" type="color" class="form-control form-control-sm" />
            </div>
          </div>

          <!-- Panel de configuración para Ratio Financiero -->
          <div v-if="draft.presentationType === 'ratio'" class="ratio-config-panel">
            <div class="ratio-config-grid">
              <div>
                <label class="form-label">Reporte</label>
                <select v-model="draft.ratioConfig.codReporte" @change="onFiltroRatioChange" class="form-select form-select-sm">
                  <option value="">Seleccione reporte</option>
                  <option v-for="r in reportes" :key="r.id" :value="r.codigo || r.id">
                    {{ r.nombre }}
                  </option>
                </select>
              </div>
              <div>
                <label class="form-label">Vigencia</label>
                <select v-model="draft.ratioConfig.vigencia" @change="onFiltroRatioChange" class="form-select form-select-sm">
                  <option value="">Seleccione vigencia</option>
                  <option v-for="f in fechas" :key="f.fecha" :value="f.fecha">{{ f.fecha }}</option>
                </select>
              </div>
              <div>
                <label class="form-label">Compañía</label>
                <select v-model="draft.ratioConfig.idCompania" @change="onFiltroRatioChange" class="form-select form-select-sm">
                  <option value="">Seleccione compañía</option>
                  <option value="todas">Todas (Consolidado General)</option>
                  <option v-for="c in companias" :key="c.id" :value="c.id">{{ c.acronym }} {{ c.nombre }}</option>
                </select>
              </div>
            </div>

            <!-- Indicador de carga o mensaje -->
            <div v-if="cargandoLineas" class="ratio-hint ratio-loading">
              <small>Cargando líneas NIIF...</small>
            </div>
            <div v-else-if="lineasReporte.length > 0" class="ratio-hint ratio-success">
              <small>✓ {{ lineasReporte.length }} líneas NIIF cargadas</small>
            </div>
            <div v-else-if="!lineasReporte.length && draft.ratioConfig.idCompania && puedeGenerarLineas && !cargandoLineas" class="ratio-hint ratio-error">
              <small>No se encontraron líneas NIIF para estos filtros.</small>
            </div>
            <div v-else-if="!lineasReporte.length && !puedeGenerarLineas && draft.ratioConfig.idCompania" class="ratio-hint">
              <small>Completa todos los filtros para cargar las líneas NIIF.</small>
            </div>

            <div class="ratio-lineas-grid">
              <div>
                <label class="form-label">Numerador</label>
                <select 
                  v-model="draft.ratioConfig.niifNumerador" 
                  @change="actualizarLabelNumerador"
                  class="form-select form-select-sm" 
                  :disabled="cargandoLineas || !lineasReporte.length"
                >
                  <option value="">Seleccione línea</option>
                  <option v-for="l in lineasReporte" :key="'num-' + l.niif" :value="l.niif">
                    {{ l.descripcion }}
                  </option>
                </select>
                <div v-if="draft.ratioConfig.labelNumerador" class="ratio-label-auto">
                  <small>📌 {{ draft.ratioConfig.labelNumerador }}</small>
                </div>
              </div>
              <div>
                <label class="form-label">Denominador</label>
                <select 
                  v-model="draft.ratioConfig.niifDenominador" 
                  @change="actualizarLabelDenominador"
                  class="form-select form-select-sm" 
                  :disabled="cargandoLineas || !lineasReporte.length"
                >
                  <option value="">Seleccione línea</option>
                  <option v-for="l in lineasReporte" :key="'den-' + l.niif" :value="l.niif">
                    {{ l.descripcion }}
                  </option>
                </select>
                <div v-if="draft.ratioConfig.labelDenominador" class="ratio-label-auto">
                  <small>📌 {{ draft.ratioConfig.labelDenominador }}</small>
                </div>
              </div>
            </div>

            <!-- Vista previa del ratio -->
            <div v-if="resultadoRatio !== null" class="ratio-preview">
              <div class="ratio-result">
                <strong>Resultado: {{ resultadoRatio.toFixed(4) }}</strong>
              </div>
              <div class="ratio-detalle">
                <small>
                  {{ formatNumber(valorNumerador) }} / {{ formatNumber(valorDenominador) }}
                  = {{ resultadoRatio.toFixed(4) }}
                </small>
              </div>
              <div class="ratio-interpretacion">
                {{ interpretacionTexto }}
              </div>
            </div>
            <div v-else-if="draft.ratioConfig.niifNumerador && draft.ratioConfig.niifDenominador && !cargandoLineas" class="ratio-preview ratio-error">
              <span>⚠️ No se puede calcular. Verifica que ambas líneas tengan saldo.</span>
            </div>
          </div>

          <div class="creator-actions">
            <button class="btn btn-info btn-sm" @click="createTag">Guardar</button>
            <button class="btn btn-outline-secondary btn-sm" @click="showCreator = false">Cancelar</button>
          </div>
        </div>
      </section>

      <!-- Malla de etiquetas -->
      <section ref="canvas" class="tags-canvas">
        <div v-if="!tags.length" class="canvas-empty">
          <p>No hay etiquetas. Crea la primera para empezar.</p>
        </div>

        <article
          v-for="tag in tags"
          :key="tag.id"
          class="mini-tag"
          :class="{
            active: activeTagId === tag.id,
            editing: editingTagId === tag.id
          }"
          :style="tagStyle(tag)"
          @pointerdown="startDrag($event, tag.id)"
        >
          <header class="mini-tag-header">
            <strong>{{ tag.title }}</strong>
            <div class="mini-tag-actions">
              <button class="mini-edit" @click.stop="openTagEditor(tag.id)">✎</button>
              <button class="mini-delete" @click.stop="deleteTag(tag.id)">×</button>
            </div>
          </header>

          <div class="mini-tag-body">
            <!-- Editor -->
            <div v-if="editingTagId === tag.id" class="inline-editor" @pointerdown.stop>
              <div class="inline-grid">
                <div class="inline-field">
                  <small>Título</small>
                  <input v-model="editDraft.title" class="form-control form-control-sm" />
                </div>
                <div class="inline-field">
                  <small>Tipo</small>
                  <select v-model="editDraft.presentationType" class="form-select form-select-sm">
                    <option value="pie">Pastel</option>
                    <option value="donut">Dona</option>
                    <option value="bars">Barras</option>
                    <option value="bars_horizontal">Barras horizontales</option>
                    <option value="line">Línea</option>
                    <option value="area">Área</option>
                    <option value="gauge">Medidor</option>
                    <option value="progress">Progreso</option>
                    <option value="ranking">Ranking</option>
                    <option value="value">Valor numérico</option>
                    <option value="ratio">Ratio Financiero</option>
                  </select>
                </div>
              </div>

              <div class="inline-color-row">
                <input v-model="editDraft.color" type="color" />
                <button class="btn btn-info btn-sm" @click="saveTagEdit">Guardar</button>
                <button class="btn btn-outline-secondary btn-sm" @click="cancelTagEdit">Cancelar</button>
              </div>
            </div>

            <!-- Renderizado de etiquetas -->
            <template v-else>
              <!-- RATIO FINANCIERO - Diseño resumido -->
              <div v-if="tag.presentationType === 'ratio'" class="ratio-display">
                <div v-if="tag.ratioData && tag.ratioData.resultado !== null" class="ratio-display-content">
                  <!-- Título -->
                  <div class="ratio-title">{{ tag.title }}</div>
                  
                  <!-- Compañía y Periodo -->
                  <div class="ratio-meta">
                    <span>Compañía: {{ tag.ratioData.nombreCompania }}</span>
                    <span>Periodo: {{ tag.ratioData.vigencia }}</span>
                  </div>
                  
                  <!-- Numerador / Denominador con valores -->
                  <div class="ratio-values">
                    <div class="ratio-value-item">
                      <span class="ratio-value-label">{{ tag.ratioData.numerador }}:</span>
                      <span class="ratio-value-number">{{ formatNumber(tag.ratioData.valorNumerador) }}</span>
                    </div>
                    <div class="ratio-value-item">
                      <span class="ratio-value-label">{{ tag.ratioData.denominador }}:</span>
                      <span class="ratio-value-number">{{ formatNumber(tag.ratioData.valorDenominador) }}</span>
                    </div>
                  </div>
                  
                  <!-- Resultado -->
                  <div class="ratio-result-display">
                    <span class="ratio-result-number">${{ tag.ratioData.resultado.toFixed(2) }}</span>
                  </div>
                  
                  <!-- Interpretación resumida -->
                  <div class="ratio-interpretacion-display">
                    <span>La empresa {{ tag.ratioData.nombreCompania }} tiene ${{ tag.ratioData.resultado.toFixed(2) }} de {{ tag.ratioData.numerador }} por cada $1 de {{ tag.ratioData.denominador }}.</span>
                  </div>
                </div>
                <div v-else-if="tag.ratioData && tag.ratioData.error" class="ratio-error-display">
                  <span>⚠️ {{ tag.ratioData.error }}</span>
                </div>
                <div v-else class="ratio-loading-display">
                  <span>Cargando ratio...</span>
                </div>
              </div>

              <!-- Gráfico de Pastel -->
              <div v-if="tag.presentationType === 'pie'" class="chart-panel">
                <div class="mini-pie" :style="{ background: pieGradient }"></div>
                <div class="mini-series-legend">
                  <span v-for="(item, idx) in pieData" :key="idx" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="metric-meta">
                  <span>Total: {{ pieData.reduce((sum, d) => sum + d.value, 0) }}</span>
                </div>
              </div>

              <!-- Gráfico de Dona -->
              <div v-if="tag.presentationType === 'donut'" class="chart-panel">
                <div class="mini-donut" :style="{ background: pieGradient }">
                  <span>{{ pieData.reduce((sum, d) => sum + d.value, 0) }}</span>
                </div>
                <div class="mini-series-legend">
                  <span v-for="(item, idx) in pieData" :key="idx" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }}
                  </span>
                </div>
              </div>

              <!-- Gráfico de Barras -->
              <div v-if="tag.presentationType === 'bars'" class="chart-panel bars-panel">
                <div class="mini-bars">
                  <div v-for="(item, idx) in barData" :key="idx" class="mini-bar-group">
                    <div class="mini-bar-item">
                      <div class="mini-bar-top-metrics">
                        <small class="mini-bar-value">{{ item.label }}</small>
                      </div>
                      <span class="mini-bar-fill" :style="{ height: `${item.height}%`, background: item.color }"></span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Gráfico de Barras Horizontales -->
              <div v-if="tag.presentationType === 'bars_horizontal'" class="chart-panel">
                <div v-for="(item, idx) in barData" :key="idx" class="hbar-row">
                  <span class="hbar-label">{{ item.label }}</span>
                  <div class="hbar-track">
                    <span class="hbar-fill" :style="{ width: `${item.height}%`, background: item.color }"></span>
                  </div>
                  <span class="hbar-value">{{ item.value }}</span>
                </div>
              </div>

              <!-- Gráfico de Línea -->
              <div v-if="tag.presentationType === 'line'" class="chart-panel">
                <div class="mini-line">
                  <svg class="line-svg" viewBox="0 0 100 100">
                    <polyline :points="linePoints" stroke="#4A90D9" stroke-width="2" fill="none" />
                    <circle v-for="(point, idx) in linePointsData" :key="idx" :cx="point.x" :cy="point.y" r="3" fill="#4A90D9" />
                  </svg>
                </div>
              </div>

              <!-- Gráfico de Área -->
              <div v-if="tag.presentationType === 'area'" class="chart-panel">
                <div class="mini-area">
                  <svg class="line-svg" viewBox="0 0 100 100">
                    <polygon :points="areaPoints" fill="rgba(74, 144, 217, 0.3)" />
                    <polyline :points="linePoints" stroke="#4A90D9" stroke-width="2" fill="none" />
                  </svg>
                </div>
              </div>

              <!-- Medidor -->
              <div v-if="tag.presentationType === 'gauge'" class="chart-panel">
                <div class="mini-gauge" :style="{ background: gaugeGradient }">
                  <span>{{ progressValue }}%</span>
                </div>
              </div>

              <!-- Progreso -->
              <div v-if="tag.presentationType === 'progress'" class="chart-panel">
                <div class="progress-track">
                  <span class="progress-fill" :style="{ width: `${progressValue}%`, background: '#4A90D9' }"></span>
                </div>
                <div class="tag-value">{{ progressValue }}%</div>
              </div>

              <!-- Ranking -->
              <div v-if="tag.presentationType === 'ranking'" class="chart-panel">
                <div v-for="(item, idx) in rankingData" :key="idx" class="rank-row">
                  <span class="rank-name">{{ item.label }}</span>
                  <strong class="rank-value">{{ item.value }}</strong>
                </div>
              </div>

              <!-- Valor numérico -->
              <div v-if="tag.presentationType === 'value'" class="chart-panel">
                <div class="tag-value">{{ randomValue }}</div>
                <div class="tag-legend">Valor de ejemplo</div>
              </div>
            </template>
          </div>

          <span class="resize-handle" @pointerdown.stop="startResize($event, tag.id)" />
        </article>
      </section>
    </b-container>
  </div>
</template>

<script>
import DashboardEtiquetas from "@/services/DashboardEtiquetas";
import Balance from "@/services/BalanceNiif";

export default {
  name: "Dashboard",
  data() {
    return {
      maxTags: 50,
      showCreator: false,
      editingTagId: null,
      editingRatioTagId: null,
      tags: [],
      activeTagId: null,
      zSeed: 10,
      interaction: null,
      saveTimeout: null,

      reportes: [],
      fechas: [],
      companias: [],
      lineasReporte: [],
      cargandoLineas: false,
      catalogosCargados: false,

      pieData: [
        { label: "Activos", value: 45, color: "#4A90D9", percent: 45 },
        { label: "Pasivos", value: 30, color: "#50C878", percent: 30 },
        { label: "Patrimonio", value: 25, color: "#FFB74D", percent: 25 }
      ],

      barData: [
        { label: "Ene", value: 100, height: 80, color: "#4A90D9" },
        { label: "Feb", value: 80, height: 65, color: "#50C878" },
        { label: "Mar", value: 60, height: 50, color: "#FFB74D" },
        { label: "Abr", value: 40, height: 35, color: "#EF5350" }
      ],

      rankingData: [
        { label: "Activos", value: 450 },
        { label: "Pasivos", value: 320 },
        { label: "Patrimonio", value: 280 },
        { label: "Ingresos", value: 200 }
      ],

      draft: {
        title: "",
        presentationType: "pie",
        color: "#f5faff",
        textColor: "#000000",
        ratioConfig: {
          codReporte: "",
          vigencia: "",
          idCompania: "",
          niifNumerador: "",
          niifDenominador: "",
          labelNumerador: "",
          labelDenominador: ""
        }
      },

      editDraft: {
        title: "",
        presentationType: "pie",
        color: "#f5faff",
        textColor: "#000000",
        ratioConfig: null
      },

      editOriginal: null,
      editOriginalRatio: null
    };
  },

  computed: {
    progressValue() {
      return Math.floor(Math.random() * 40) + 60;
    },

    randomValue() {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0
      }).format(Math.floor(Math.random() * 1000000000) + 1000000);
    },

    linePointsData() {
      const points = [20, 40, 30, 60, 50, 80, 70, 90];
      return points.map((value, index) => ({
        x: (index / (points.length - 1)) * 100,
        y: 100 - (value / 100) * 80
      }));
    },

    linePoints() {
      return this.linePointsData.map(p => `${p.x},${p.y}`).join(" ");
    },

    areaPoints() {
      const data = this.linePointsData;
      return `${data[0].x},100 ${this.linePoints} ${data[data.length-1].x},100`;
    },

    pieGradient() {
      const total = this.pieData.reduce((s, d) => s + d.value, 0);
      let cursor = 0;
      const steps = this.pieData.map(d => {
        const percent = (d.value / total) * 100;
        const from = cursor;
        const to = cursor + percent;
        cursor = to;
        return `${d.color} ${from}% ${to}%`;
      });
      return `conic-gradient(${steps.join(", ")})`;
    },

    gaugeGradient() {
      const pct = this.progressValue;
      return `conic-gradient(from 180deg, #4A90D9 0% ${pct}%, rgba(255,255,255,0.2) ${pct}% 100%)`;
    },

    puedeGenerarLineas() {
      const c = this.draft.ratioConfig;
      return !!(c.codReporte && c.vigencia && c.idCompania);
    },

    valorNumerador() {
      const l = this.lineasReporte.find(l => l.niif === this.draft.ratioConfig.niifNumerador);
      return l ? l.valor : null;
    },

    valorDenominador() {
      const l = this.lineasReporte.find(l => l.niif === this.draft.ratioConfig.niifDenominador);
      return l ? l.valor : null;
    },

    resultadoRatio() {
      if (this.valorNumerador === null || this.valorDenominador === null) return null;
      if (this.valorDenominador === 0) return null;
      return this.valorNumerador / this.valorDenominador;
    },

    nombreCompaniaTag() {
      if (this.draft.ratioConfig.idCompania === "todas") return "Consolidado General";
      const c = this.companias.find(c => c.id == this.draft.ratioConfig.idCompania);
      return c ? c.nombre : "";
    },

    interpretacionTexto() {
      if (this.resultadoRatio === null) return "";
      const valor = this.resultadoRatio.toFixed(4);
      const lblNum = this.draft.ratioConfig.labelNumerador || "este indicador";
      const lblDen = this.draft.ratioConfig.labelDenominador || "este otro indicador";
      return `La empresa ${this.nombreCompaniaTag} tiene $${valor} de ${lblNum} por cada $1 de ${lblDen}.`;
    }
  },

  mounted() {
    this.loadTags();
    this.cargarCatalogos();
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  },

  beforeUnmount() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  },

  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    actualizarLabelNumerador() {
      const niif = this.draft.ratioConfig.niifNumerador;
      const linea = this.lineasReporte.find(l => l.niif === niif);
      this.draft.ratioConfig.labelNumerador = linea ? linea.descripcion : '';
    },

    actualizarLabelDenominador() {
      const niif = this.draft.ratioConfig.niifDenominador;
      const linea = this.lineasReporte.find(l => l.niif === niif);
      this.draft.ratioConfig.labelDenominador = linea ? linea.descripcion : '';
    },

    async cargarCatalogos() {
      try {
        const [reportesRes, fechasRes, companiasRes] = await Promise.all([
          Balance.getReportes(),
          Balance.getFechas(),
          Balance.getCompanias(true)
        ]);

        this.reportes = reportesRes.data.reportes || [];
        this.fechas = fechasRes.data.fechas || [];
        this.companias = companiasRes.data.companias || [];
        this.catalogosCargados = true;

        console.log('Reportes cargados:', this.reportes.length);
        console.log('Fechas cargadas:', this.fechas.length);
        console.log('Compañías cargadas:', this.companias.length);

      } catch (error) {
        console.error("Error cargando catálogos:", error);
        if (this.$toast) {
          this.$toast.error("Error al cargar catálogos");
        }
      }
    },

    onFiltroRatioChange() {
      this.lineasReporte = [];
      this.draft.ratioConfig.niifNumerador = "";
      this.draft.ratioConfig.niifDenominador = "";
      this.draft.ratioConfig.labelNumerador = "";
      this.draft.ratioConfig.labelDenominador = "";

      if (this.puedeGenerarLineas) {
        console.log('Filtros completos, cargando líneas...');
        console.log('Reporte seleccionado:', this.draft.ratioConfig.codReporte);
        this.cargarLineasReporte();
      }
    },

    async cargarLineasReporte() {
      const { codReporte, vigencia, idCompania } = this.draft.ratioConfig;

      if (!codReporte || !vigencia || !idCompania) {
        this.lineasReporte = [];
        return;
      }

      this.cargandoLineas = true;
      this.lineasReporte = [];

      try {
        const empresasIds = idCompania === "todas"
          ? this.companias.map(c => c.id)
          : [parseInt(idCompania)];

        console.log('Llamando a getBalanceNiifConsol con:', {
          codReporte,
          vigencia: parseInt(vigencia),
          empresasIds,
          moneda: 1
        });

        const result = await Balance.getBalanceNiifConsol(
          codReporte,
          parseInt(vigencia),
          empresasIds,
          1
        );

        console.log('Respuesta de getBalanceNiifConsol:', result.data);

        const filas = (result.data.balanceNiif || []).map(item => ({
          niif: item.niif,
          descripcion: item.descripcion,
          informacion: item.informacion,
          valor: parseFloat(item.total) || 0
        }));

        this.lineasReporte = filas.filter(f => f.informacion !== "E");
        
        console.log('Líneas NIIF cargadas (Consolidado):', this.lineasReporte.length);

        if (this.lineasReporte.length === 0 && this.$toast) {
          this.$toast.warning("No se encontraron líneas NIIF para estos filtros");
        }

      } catch (error) {
        console.error("Error cargando líneas NIIF:", error);
        if (this.$toast) {
          this.$toast.error("No se pudo cargar el reporte: " + (error.message || ''));
        }
      } finally {
        this.cargandoLineas = false;
      }
    },

    async loadTags() {
      try {
        const res = await DashboardEtiquetas.getAll();
        const parsed = res?.data?.listDashboardEtiquetas || [];
        
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.tags = parsed.map((tag, index) => {
            const tagData = {
              id: tag.id || Date.now() + index,
              title: tag.titulo || "Etiqueta",
              presentationType: tag.presentation_type || "pie",
              color: tag.color_fondo || "#f5faff",
              textColor: tag.color_texto || "#000000",
              left: tag.pos_x || 20 + (index * 20),
              top: tag.pos_y || 20 + (index * 20),
              width: tag.width_px || 240,
              height: tag.height_px || 180,
              zIndex: tag.z_index || index + 1,
              ratioConfig: null,
              ratioData: null
            };

            if (tag.presentation_type === "ratio" && tag.info_key) {
              try {
                tagData.ratioConfig = JSON.parse(tag.info_key);
                console.log('Ratio config cargado:', tagData.ratioConfig);
              } catch (e) {
                console.error("Error parseando ratioConfig:", e);
              }
            }

            return tagData;
          });
          
          this.zSeed = this.tags.reduce((max, t) => Math.max(max, t.zIndex), 10);

          if (this.catalogosCargados) {
            this.cargarDatosRatios();
          } else {
            const checkCatalogs = setInterval(() => {
              if (this.catalogosCargados) {
                clearInterval(checkCatalogs);
                this.cargarDatosRatios();
              }
            }, 500);
          }
        } else {
          this.tags = [];
          this.zSeed = 10;
        }
      } catch (error) {
        console.error("Error cargando etiquetas:", error);
        this.tags = [];
        this.zSeed = 10;
      }
    },

    async cargarDatosRatios() {
      console.log('Cargando datos de ratios...');
      for (const tag of this.tags) {
        if (tag.presentationType === "ratio" && tag.ratioConfig) {
          await this.calcularRatioTag(tag);
        }
      }
    },

    async calcularRatioTag(tag) {
      try {
        const config = tag.ratioConfig;
        if (!config || !config.codReporte || !config.vigencia || !config.idCompania) {
          tag.ratioData = { error: "Configuración incompleta" };
          return;
        }

        console.log(`Calculando ratio para tag ${tag.id}:`, config);

        const empresasIds = config.idCompania === "todas"
          ? this.companias.map(c => c.id)
          : [parseInt(config.idCompania)];

        const result = await Balance.getBalanceNiifConsol(
          config.codReporte,
          parseInt(config.vigencia),
          empresasIds,
          1
        );

        const filas = (result.data.balanceNiif || []).map(item => ({
          niif: item.niif,
          descripcion: item.descripcion,
          valor: parseFloat(item.total) || 0
        }));

        const numerador = filas.find(l => l.niif === config.niifNumerador);
        const denominador = filas.find(l => l.niif === config.niifDenominador);

        if (!numerador || !denominador) {
          tag.ratioData = { error: "Línea(s) no encontrada(s)" };
          return;
        }

        if (denominador.valor === 0) {
          tag.ratioData = { error: "Denominador = 0" };
          return;
        }

        const resultado = numerador.valor / denominador.valor;

        const nombreCompania = config.idCompania === "todas"
          ? "Consolidado General"
          : (this.companias.find(c => c.id == config.idCompania)?.nombre || "");

        const lblNum = config.labelNumerador || numerador.descripcion;
        const lblDen = config.labelDenominador || denominador.descripcion;

        tag.ratioData = {
          resultado: resultado,
          valorNumerador: numerador.valor,
          valorDenominador: denominador.valor,
          numerador: lblNum,
          denominador: lblDen,
          nombreCompania: nombreCompania,
          vigencia: config.vigencia,
          interpretacion: `La empresa ${nombreCompania} tiene $${resultado.toFixed(2)} de ${lblNum} por cada $1 de ${lblDen}.`
        };

        console.log(`Ratio calculado para tag ${tag.id}:`, tag.ratioData);

      } catch (error) {
        console.error("Error calculando ratio:", error);
        tag.ratioData = { error: "Error al calcular" };
      }
    },

    toggleCreator() {
      this.showCreator = !this.showCreator;
      if (this.showCreator) {
        this.cancelTagEdit();
        this.draft.ratioConfig = {
          codReporte: "",
          vigencia: "",
          idCompania: "",
          niifNumerador: "",
          niifDenominador: "",
          labelNumerador: "",
          labelDenominador: ""
        };
        this.lineasReporte = [];
      }
    },

    async createTag() {
      if (this.tags.length >= this.maxTags) {
        alert("Límite de etiquetas alcanzado");
        return;
      }

      if (!this.draft.title || this.draft.title.trim() === "") {
        alert("Por favor, ingresa un título para la etiqueta");
        return;
      }

      const freePosition = this.findAvailablePosition(240, 180);
      if (!freePosition) {
        alert("No hay espacio disponible");
        return;
      }

      const tag = {
        title: this.draft.title.trim(),
        presentationType: this.draft.presentationType,
        color: this.draft.color,
        textColor: this.draft.textColor,
        left: freePosition.left,
        top: freePosition.top,
        width: 240,
        height: 180,
        zIndex: ++this.zSeed,
        ratioConfig: null
      };

      let infoKey = null;
      if (this.draft.presentationType === "ratio") {
        const config = this.draft.ratioConfig;
        if (config.codReporte && config.vigencia && config.idCompania &&
            config.niifNumerador && config.niifDenominador) {
          tag.ratioConfig = { ...config };
          infoKey = JSON.stringify(config);
        } else {
          alert("Por favor, completa toda la configuración del ratio");
          return;
        }
      }

      try {
        const payload = {
          titulo: tag.title,
          presentation_type: tag.presentationType,
          color_fondo: tag.color,
          color_texto: tag.textColor,
          pos_x: tag.left,
          pos_y: tag.top,
          width_px: tag.width,
          height_px: tag.height,
          z_index: tag.zIndex,
          idUser: this.$store.state.userId || 1,
          info_key: infoKey
        };

        const res = await DashboardEtiquetas.create(payload);
        tag.id = res?.data?.id || Date.now();

        if (tag.presentationType === "ratio" && tag.ratioConfig) {
          await this.calcularRatioTag(tag);
        }

        this.tags.push(tag);
        this.showCreator = false;
        this.draft = {
          title: "",
          presentationType: "pie",
          color: "#f5faff",
          textColor: "#000000",
          ratioConfig: {
            codReporte: "",
            vigencia: "",
            idCompania: "",
            niifNumerador: "",
            niifDenominador: "",
            labelNumerador: "",
            labelDenominador: ""
          }
        };

        if (this.$toast) {
          this.$toast.success("Etiqueta creada correctamente");
        }
      } catch (error) {
        console.error("Error creando etiqueta:", error);
        alert("Error al crear la etiqueta. Por favor, intenta de nuevo.");
      }
    },

    async deleteTag(tagId) {
      if (!confirm("¿Eliminar esta etiqueta?")) return;
      try {
        await DashboardEtiquetas.delete(tagId);
        this.tags = this.tags.filter(t => t.id !== tagId);
        if (this.$toast) {
          this.$toast.success("Etiqueta eliminada correctamente");
        }
      } catch (error) {
        console.error("Error eliminando etiqueta:", error);
        alert("Error al eliminar la etiqueta. Por favor, intenta de nuevo.");
      }
    },

    openTagEditor(tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      this.editingTagId = tagId;
      this.editDraft = {
        title: tag.title,
        presentationType: tag.presentationType,
        color: tag.color,
        textColor: tag.textColor,
        ratioConfig: tag.ratioConfig ? { ...tag.ratioConfig } : null
      };
      this.editOriginal = { ...this.editDraft };
    },

    cancelTagEdit() {
      this.editingTagId = null;
      this.editingRatioTagId = null;
      this.editOriginal = null;
      this.editOriginalRatio = null;
    },

    async saveTagEdit() {
      const tag = this.tags.find(t => t.id === this.editingTagId);
      if (!tag) return;

      if (!this.editDraft.title || this.editDraft.title.trim() === "") {
        alert("El título no puede estar vacío");
        return;
      }

      let infoKey = null;
      if (this.editDraft.presentationType === "ratio" && this.editDraft.ratioConfig) {
        const config = this.editDraft.ratioConfig;
        if (config.codReporte && config.vigencia && config.idCompania &&
            config.niifNumerador && config.niifDenominador) {
          infoKey = JSON.stringify(config);
        }
      }

      tag.title = this.editDraft.title.trim();
      tag.presentationType = this.editDraft.presentationType;
      tag.color = this.editDraft.color;
      tag.textColor = this.editDraft.textColor;
      tag.ratioConfig = this.editDraft.ratioConfig ? { ...this.editDraft.ratioConfig } : null;

      if (tag.presentationType === "ratio" && tag.ratioConfig) {
        await this.calcularRatioTag(tag);
      }

      await this.persistTag(tag, infoKey);
      this.cancelTagEdit();

      if (this.$toast) {
        this.$toast.success("Etiqueta actualizada correctamente");
      }
    },

    async persistTag(tag, overrideInfoKey = null) {
      if (!tag || !tag.id) return;

      try {
        let infoKey = overrideInfoKey;
        if (infoKey === null && tag.presentationType === "ratio" && tag.ratioConfig) {
          infoKey = JSON.stringify(tag.ratioConfig);
        }

        await DashboardEtiquetas.update({
          id: tag.id,
          titulo: tag.title,
          presentation_type: tag.presentationType,
          color_fondo: tag.color,
          color_texto: tag.textColor,
          pos_x: Math.round(tag.left),
          pos_y: Math.round(tag.top),
          width_px: Math.round(tag.width),
          height_px: Math.round(tag.height),
          z_index: tag.zIndex,
          idUser: this.$store.state.userId || 1,
          info_key: infoKey || null
        });
      } catch (error) {
        console.error('Error guardando etiqueta:', error);
      }
    },

    startDrag(event, tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      if (this.editingTagId) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "drag",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        left: tag.left,
        top: tag.top
      };
    },

    startResize(event, tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      if (this.editingTagId) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "resize",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        width: tag.width,
        height: tag.height
      };
    },

    onPointerMove(event) {
      if (!this.interaction) return;
      const tag = this.tags.find(t => t.id === this.interaction.tagId);
      if (!tag || !this.$refs.canvas) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const deltaX = event.clientX - this.interaction.startX;
      const deltaY = event.clientY - this.interaction.startY;

      if (this.interaction.type === "drag") {
        const maxLeft = Math.max(canvasRect.width - tag.width, 0);
        const maxTop = Math.max(canvasRect.height - tag.height, 0);
        tag.left = Math.max(0, Math.min(this.interaction.left + deltaX, maxLeft));
        tag.top = Math.max(0, Math.min(this.interaction.top + deltaY, maxTop));
        this.scheduleAutoSave(tag);
      } else if (this.interaction.type === "resize") {
        tag.width = Math.max(200, Math.min(this.interaction.width + deltaX, 600));
        tag.height = Math.max(150, Math.min(this.interaction.height + deltaY, 400));
        this.scheduleAutoSave(tag);
      }
    },

    onPointerUp() {
      if (!this.interaction) return;
      const tag = this.tags.find(t => t.id === this.interaction.tagId);
      if (tag) {
        this.persistTag(tag);
      }
      this.interaction = null;
    },

    scheduleAutoSave(tag) {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.persistTag(tag);
        this.saveTimeout = null;
      }, 300);
    },

    raiseTag(tag) {
      tag.zIndex = ++this.zSeed;
    },

    findAvailablePosition(width, height) {
      const canvas = this.$refs.canvas;
      if (!canvas) return { left: 20, top: 20 };

      const maxLeft = Math.max(canvas.clientWidth - width, 0);
      const maxTop = Math.max(canvas.clientHeight - height, 0);

      for (let top = 10; top <= maxTop; top += 20) {
        for (let left = 10; left <= maxLeft; left += 20) {
          const candidate = { left, top, width, height };
          if (!this.hasCollision(candidate)) {
            return { left, top };
          }
        }
      }
      return null;
    },

    hasCollision(candidate) {
      return this.tags.some(tag => {
        return candidate.left < tag.left + tag.width &&
               candidate.left + candidate.width > tag.left &&
               candidate.top < tag.top + tag.height &&
               candidate.top + candidate.height > tag.top;
      });
    },

    getCompaniaNombre(id) {
      if (id === "todas") return "Consolidado General";
      const c = this.companias.find(c => c.id == id);
      return c ? c.nombre : "";
    },

    getLineaDescripcion(niif) {
      const l = this.lineasReporte.find(l => l.niif === niif);
      return l ? l.descripcion : niif;
    },

    calcularRatioEdit() {
      if (!this.editDraft.ratioConfig) return "N/A";
      const config = this.editDraft.ratioConfig;
      const num = this.lineasReporte.find(l => l.niif === config.niifNumerador);
      const den = this.lineasReporte.find(l => l.niif === config.niifDenominador);
      if (!num || !den || den.valor === 0) return "N/A";
      return (num.valor / den.valor).toFixed(4);
    },

    openFullRatioEditor(tagId) {
      this.editingRatioTagId = tagId;
    },

    cancelRatioEdit() {
      this.editingRatioTagId = null;
    },

    tagStyle(tag) {
      return {
        left: `${tag.left}px`,
        top: `${tag.top}px`,
        width: `${tag.width}px`,
        height: `${tag.height}px`,
        backgroundColor: tag.color,
        color: tag.textColor,
        zIndex: tag.zIndex
      };
    }
  }
};
</script>

<style scoped>
.dashboard-lab-container {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.outside-toolbar {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
}

.outside-toolbar .btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-weight: 600;
  border-radius: 8px;
  background: #4A90D9;
  border: none;
  color: white;
  transition: all 0.2s;
}

.outside-toolbar .btn:hover {
  background: #3a7bc8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 217, 0.3);
}

.outside-toolbar .btn .material-icons {
  font-size: 18px;
}

.tag-counter {
  font-size: 14px;
  color: #6c757d;
  background: #f1f3f5;
  padding: 5px 14px;
  border-radius: 20px;
  font-weight: 500;
}

.creator-panel {
  margin-bottom: 20px;
  animation: slideDown 0.3s ease;
}

.creator-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
}

.creator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 0.5fr;
  gap: 15px;
  margin: 12px 0;
}

.creator-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.tags-canvas {
  position: relative;
  min-height: 600px;
  background: white;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.canvas-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #adb5bd;
  font-size: 1.1rem;
}

.mini-tag {
  position: absolute;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
  border: 1px solid rgba(0,0,0,0.06);
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: grab;
}

.mini-tag:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.16);
}

.mini-tag.active {
  box-shadow: 0 0 0 2px #4A90D9, 0 4px 16px rgba(74, 144, 217, 0.25);
}

.mini-tag.editing {
  box-shadow: 0 0 0 2px #FFB74D, 0 4px 16px rgba(255, 183, 77, 0.25);
  z-index: 999 !important;
}

.mini-tag-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.mini-tag-header strong {
  font-size: 13px;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-tag-actions {
  display: flex;
  gap: 4px;
}

.mini-edit,
.mini-delete {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  font-size: 14px;
  color: #6c757d;
  border-radius: 4px;
  transition: all 0.2s;
}

.mini-edit:hover {
  background: rgba(74, 144, 217, 0.15);
  color: #4A90D9;
}

.mini-delete:hover {
  background: rgba(239, 83, 80, 0.15);
  color: #EF5350;
}

.mini-tag-body {
  flex: 1;
  overflow: auto;
  font-size: 12px;
}

.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  opacity: 0.3;
  transition: opacity 0.2s;
}

.resize-handle::after {
  content: "";
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-right: 2px solid #6c757d;
  border-bottom: 2px solid #6c757d;
}

.resize-handle:hover {
  opacity: 0.8;
}

.inline-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inline-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.inline-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inline-field small {
  font-size: 10px;
  color: #6c757d;
}

.inline-color-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.inline-color-row input[type="color"] {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
}

.chart-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  height: 100%;
  justify-content: center;
}

.mini-pie,
.mini-donut {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mini-donut {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.mini-donut::after {
  content: "";
  position: absolute;
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
}

.mini-donut span {
  position: relative;
  z-index: 1;
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
}

.mini-series-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  justify-content: center;
}

.series-chip {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  color: #495057;
}

.series-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.mini-bars {
  display: flex;
  gap: 8px;
  height: 80px;
  align-items: flex-end;
  width: 100%;
  justify-content: center;
}

.mini-bar-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
  max-width: 40px;
}

.mini-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 100%;
}

.mini-bar-top-metrics {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mini-bar-value {
  font-size: 8px;
  color: #6c757d;
}

.mini-bar-fill {
  width: 100%;
  border-radius: 4px 4px 0 0;
  min-height: 8px;
  max-width: 30px;
}

.hbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-size: 10px;
}

.hbar-label {
  min-width: 50px;
  font-size: 9px;
  color: #495057;
}

.hbar-track {
  flex: 1;
  height: 16px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.hbar-fill {
  display: block;
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s;
}

.hbar-value {
  font-size: 9px;
  font-weight: bold;
  min-width: 40px;
  text-align: right;
}

.mini-line,
.mini-area {
  width: 100%;
  height: 80px;
  position: relative;
}

.line-svg {
  width: 100%;
  height: 100%;
}

.metric-meta {
  display: flex;
  gap: 12px;
  font-size: 9px;
  color: #6c757d;
}

.tag-value {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.tag-legend {
  font-size: 10px;
  color: #6c757d;
}

.progress-track {
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  overflow: hidden;
}

.progress-fill {
  display: block;
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s;
}

.mini-gauge {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.rank-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 2px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  font-size: 10px;
}

.rank-name {
  color: #495057;
}

.rank-value {
  color: #2c3e50;
}

/* ===== ESTILOS PARA RATIO ===== */
.ratio-config-panel {
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.ratio-config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.ratio-lineas-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.ratio-label-auto {
  margin-top: 4px;
  padding: 4px 8px;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 11px;
  color: #495057;
}

.ratio-hint {
  color: #6c757d;
  font-size: 12px;
  padding: 4px 0;
  margin-bottom: 8px;
}

.ratio-hint small {
  font-style: italic;
}

.ratio-hint.ratio-loading {
  color: #4A90D9;
}

.ratio-hint.ratio-success {
  color: #28a745;
}

.ratio-hint.ratio-error {
  color: #dc3545;
}

.ratio-preview {
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  margin-top: 8px;
}

.ratio-result {
  font-size: 18px;
  font-weight: bold;
  color: #28a745;
}

.ratio-detalle {
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
}

.ratio-interpretacion {
  font-size: 12px;
  color: #495057;
  margin-top: 4px;
}

.ratio-error {
  color: #dc3545;
}

/* ===== DISPLAY DE RATIO EN ETIQUETA (Diseño resumido) ===== */
.ratio-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 6px 8px;
}

.ratio-display-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 100%;
}

.ratio-title {
  font-size: 14px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 4px;
}

.ratio-meta {
  display: flex;
  gap: 12px;
  font-size: 9px;
  color: #6c757d;
}

.ratio-meta span {
  background: rgba(0,0,0,0.04);
  padding: 1px 8px;
  border-radius: 10px;
}

.ratio-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 4px 8px;
  background: rgba(0,0,0,0.03);
  border-radius: 6px;
}

.ratio-value-item {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  padding: 1px 0;
}

.ratio-value-label {
  color: #495057;
  font-weight: 500;
}

.ratio-value-number {
  color: #2c3e50;
  font-weight: 600;
}

.ratio-result-display {
  margin: 2px 0;
}

.ratio-result-number {
  font-size: 24px;
  font-weight: bold;
  color: #28a745;
}

.ratio-interpretacion-display {
  font-size: 9px;
  color: #495057;
  max-width: 100%;
  word-wrap: break-word;
  padding: 3px 6px;
  background: rgba(40, 167, 69, 0.08);
  border-radius: 4px;
  line-height: 1.3;
}

.ratio-error-display {
  color: #dc3545;
  font-size: 12px;
}

.ratio-loading-display {
  color: #6c757d;
  font-size: 12px;
}

/* Editor inline ratio */
.inline-ratio-editor {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  margin: 4px 0;
}

.inline-ratio-config {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-ratio-row {
  display: flex;
  gap: 8px;
  font-size: 10px;
  color: #495057;
  flex-wrap: wrap;
}

.inline-ratio-result {
  font-size: 14px;
  font-weight: bold;
  color: #28a745;
  margin: 4px 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .creator-grid {
    grid-template-columns: 1fr;
  }

  .ratio-config-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .ratio-lineas-grid {
    grid-template-columns: 1fr;
  }

  .mini-tag {
    position: relative !important;
    margin-bottom: 12px;
    width: 100% !important;
    height: auto !important;
    min-height: 180px;
    left: 0 !important;
    top: 0 !important;
  }

  .tags-canvas {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: auto;
  }

  .resize-handle {
    display: none;
  }

  .inline-grid {
    grid-template-columns: 1fr;
  }

  .ratio-meta {
    flex-direction: column;
    gap: 2px;
  }
}
</style>